"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, TrendingUp, ShieldCheck, Target, Calendar } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const highlights = [{
  title: "B2B SEO services",
  desc: "Tailored search strategy for how businesses research and shortlist suppliers."
}, {
  title: "B2B lead generation",
  desc: "Organic visibility built around qualified enquiries, not vanity traffic."
}, {
  title: "Buyer-intent search",
  desc: "Commercial queries mapped to the decision-makers who use them."
}];
export default function B2bLeadHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="b2bleadgenerationhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28 text-foreground">
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 -left-24 h-80 w-80 rounded-full bg-secondary/40 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div variants={itemVariants}>
              <Badge variant="secondary" className="mb-6 px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full bg-secondary text-secondary-foreground border border-border flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span>Independent B2B SEO Consultant</span>
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-[1.15] mb-6">
              B2B SEO Consultant
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg md:text-xl text-muted-foreground font-default max-w-2xl mb-4 leading-relaxed">
              I help B2B companies generate qualified, sales-ready enquiries through organic search, not just raw visit counts. If your website attracts traffic but struggles to deliver the leads your sales team wants to speak to, that is the gap I close.
            </motion.p>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground font-default max-w-2xl mb-8 leading-relaxed">
              I work directly with founders, managing directors, marketing directors and sales leaders in the UK and international English-speaking markets.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-4">
              <Button asChild size="lg" className="rounded-full bg-primary text-primary-foreground shadow-md hover:bg-primary/90 px-8 py-6 text-base font-semibold cursor-pointer">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="mr-2 h-5 w-5" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-full border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground px-8 py-6 text-base font-semibold cursor-pointer">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-8">
              <Link to="/ContactPage?topic=free-seo-audit" className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors">
                Get a Free SEO Audit
              </Link>
            </motion.div>

            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full pt-4 border-t border-border">
              {highlights.map((item, i) => <div key={i} data-index={i} className="flex flex-col space-y-1.5">
                  <div className="flex items-center gap-2 font-semibold text-sm text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {item.desc}
                  </p>
                </div>)}
            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="lg:col-span-5 relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md lg:max-w-none">
              <div className="relative bg-card rounded-2xl border border-border p-3 sm:p-4 shadow-xl overflow-hidden">
                <div className="relative rounded-xl overflow-hidden aspect-4/5 bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Independent B2B SEO Consultant" loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent" />

                  <div className="absolute bottom-4 left-4 right-4 text-background">
                    <p className="text-xs font-medium tracking-wide uppercase text-accent-foreground/90">
                      B2B SEO Consultant
                    </p>
                    <p className="text-lg font-bold font-default text-background">
                      Yassine Essaadi
                    </p>
                    <p className="text-xs text-background/80">
                      Independent SEO consultant
                    </p>
                  </div>
                </div>

                <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.6,
                duration: 0.5
              }} className="absolute -top-4 -left-4 sm:-left-6 bg-background border border-border rounded-xl p-3.5 shadow-lg flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">Focus</p>
                    <p className="text-sm font-bold text-foreground">Qualified B2B enquiries</p>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.7,
                duration: 0.5
              }} className="absolute -bottom-4 -right-4 sm:-right-6 bg-background border border-border rounded-xl p-3.5 shadow-lg flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-accent/20 text-accent-foreground">
                    <Target className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">Buyer intent</p>
                    <p className="text-sm font-bold text-foreground">Decision-maker searches</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}